export const formatWorkHours = (minutes) => {
  if (!minutes) return "0h 0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
};

// Returns total minutes
export const getWorkingHoursSummary = (records, targetUserEmail = null, selectedMonthDate = null) => {
  const now = new Date();
  
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const startOfWeek = new Date(now);
  const dayOfWeek = startOfWeek.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  startOfWeek.setDate(startOfWeek.getDate() - daysToMonday);

  const monthRef = selectedMonthDate || now;
  const startOfMonth = new Date(monthRef.getFullYear(), monthRef.getMonth(), 1);
  const endOfMonth = new Date(monthRef.getFullYear(), monthRef.getMonth() + 1, 0);

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
  const startOfWeekStr = `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`;
  const startOfMonthStr = `${startOfMonth.getFullYear()}-${String(startOfMonth.getMonth() + 1).padStart(2, '0')}-${String(startOfMonth.getDate()).padStart(2, '0')}`;
  const endOfMonthStr = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, '0')}-${String(endOfMonth.getDate()).padStart(2, '0')}`;

  let thisMonthMinutes = 0;
  let thisWeekMinutes = 0;
  let yesterdayMinutes = 0;
  let thisMonthPresentDays = 0;

  records.forEach(record => {
    if (targetUserEmail && record.userEmail?.toLowerCase() !== targetUserEmail.toLowerCase()) {
      return;
    }

    if (!record.date) return;
    const workMinutes = record.totalWorkMinutes || 0;

    // Check if yesterday
    if (record.date === yesterdayStr) {
      yesterdayMinutes += workMinutes;
    }

    // Check if this week
    if (record.date >= startOfWeekStr && record.date <= todayStr) {
      thisWeekMinutes += workMinutes;
    }

    // Check if this month (based on selectedMonthDate)
    if (record.date >= startOfMonthStr && record.date <= endOfMonthStr) {
      thisMonthMinutes += workMinutes;
      if (workMinutes > 0 || record.manuallyAdjustedByAdmin) {
        thisMonthPresentDays++;
      }
    }
  });

  return {
    yesterdayMinutes,
    thisWeekMinutes,
    thisMonthMinutes,
    thisMonthPresentDays
  };
};

export const calculateLeaveBalance = (accountCreatedAt, approvedLeaves) => {
  let start = null;
  if (accountCreatedAt && typeof accountCreatedAt.toDate === 'function') {
    start = accountCreatedAt.toDate();
  } else if (accountCreatedAt) {
    start = new Date(accountCreatedAt);
  } else {
    // Fallback to start of the current year if creation date is unknown
    start = new Date(new Date().getFullYear(), 0, 1);
  }
  
  if (isNaN(start.getTime())) {
    start = new Date(new Date().getFullYear(), 0, 1);
  }

  const now = new Date();
  
  // Calculate leaves taken per month mapping
  const leavesTakenPerMonth = {};
  
  if (approvedLeaves && Array.isArray(approvedLeaves)) {
    approvedLeaves.forEach(leave => {
      if (leave.status !== "approved") return;
      
      const from = new Date(leave.fromDate);
      const to = new Date(leave.toDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      
      let dayIterator = new Date(from);
      while (dayIterator <= to) {
        // Skip weekends for leave tracking (Optional? Standard is leave applies to workdays)
        // Usually HR systems count weekends if they are in between, but often they don't.
        // The prompt did not specify. We will count all days between fromDate and toDate.
        const key = `${dayIterator.getFullYear()}-${dayIterator.getMonth()}`;
        leavesTakenPerMonth[key] = (leavesTakenPerMonth[key] || 0) + 1;
        
        dayIterator.setDate(dayIterator.getDate() + 1);
      }
    });
  }

  let currentBalance = 0;
  let currentMonthIterator = new Date(start.getFullYear(), start.getMonth(), 1);
  const currentMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);
  
  while (currentMonthIterator <= currentMonthEnd) {
    // 1. Add 1 leave for this month
    currentBalance += 1;
    
    // 2. No cap on leaves, allow unlimited carry forward
    
    // 3. Subtract leaves taken in this specific month
    const key = `${currentMonthIterator.getFullYear()}-${currentMonthIterator.getMonth()}`;
    const takenInMonth = leavesTakenPerMonth[key] || 0;
    
    currentBalance -= takenInMonth;
    
    currentMonthIterator.setMonth(currentMonthIterator.getMonth() + 1);
  }
  
  return currentBalance;
};

export const getLeaveStatistics = (accountCreatedAt, approvedLeaves) => {
  let start = null;
  if (accountCreatedAt && typeof accountCreatedAt.toDate === 'function') {
    start = accountCreatedAt.toDate();
  } else if (accountCreatedAt) {
    start = new Date(accountCreatedAt);
  } else {
    // Fallback to start of the current year if creation date is unknown
    start = new Date(new Date().getFullYear(), 0, 1);
  }
  
  if (isNaN(start.getTime())) {
    start = new Date(new Date().getFullYear(), 0, 1);
  }

  const now = new Date();
  const leavesTakenPerMonth = {};
  let totalTaken = 0;
  
  if (approvedLeaves && Array.isArray(approvedLeaves)) {
    approvedLeaves.forEach(leave => {
      if (leave.status !== "approved") return;
      
      const from = new Date(leave.fromDate);
      const to = new Date(leave.toDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(0, 0, 0, 0);
      
      let dayIterator = new Date(from);
      while (dayIterator <= to) {
        const key = `${dayIterator.getFullYear()}-${dayIterator.getMonth()}`;
        leavesTakenPerMonth[key] = (leavesTakenPerMonth[key] || 0) + 1;
        totalTaken++;
        dayIterator.setDate(dayIterator.getDate() + 1);
      }
    });
  }

  let currentBalance = 0;
  let totalEntitled = 0;
  let carriedForward = 0;
  
  let currentMonthIterator = new Date(start.getFullYear(), start.getMonth(), 1);
  const currentMonthEnd = new Date(now.getFullYear(), now.getMonth(), 1);
  
  while (currentMonthIterator <= currentMonthEnd) {
    totalEntitled += 1;
    currentBalance += 1;
    
    // Allow unlimited carry forward without cap
    
    const key = `${currentMonthIterator.getFullYear()}-${currentMonthIterator.getMonth()}`;
    const takenInMonth = leavesTakenPerMonth[key] || 0;
    
    currentBalance -= takenInMonth;
    
    // Calculate carry forward for next month (what remains at the end of THIS month)
    if (currentMonthIterator.getFullYear() === now.getFullYear() && currentMonthIterator.getMonth() === now.getMonth() - 1) {
      carriedForward = Math.max(0, currentBalance);
    }
    
    currentMonthIterator.setMonth(currentMonthIterator.getMonth() + 1);
  }
  
  return {
    balance: currentBalance,
    totalTaken,
    totalEntitled,
    carriedForward: carriedForward // Unlimited carry forward
  };
};

export const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDay = firstDay.getDay(); // 0 (Sun) to 6 (Sat)
  
  const days = [];
  
  // Padding for previous month
  for (let i = 0; i < startingDay; i++) {
    days.push(null);
  }
  
  // Actual days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }
  
  return days;
};

// Expected hours per day
export const EXPECTED_MINUTES_WEEKDAY = 510; // 8h 30m
export const EXPECTED_MINUTES_SATURDAY = 255; // 4h 15m (Half Day)

export const calculateCalendarStatus = (date, attendanceRecords, approvedLeaves, accountCreatedAt, holidays = []) => {
  if (!date) return { status: 'EMPTY' };
  
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  
  // Normalize date to 00:00:00 for comparison
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  const isFuture = checkDate > now;
  
  const dayOfWeek = checkDate.getDay();
  const isSunday = dayOfWeek === 0;
  const isSaturday = dayOfWeek === 6;

  const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;
  
  // 1. Check if Holiday
  const holiday = holidays.find(h => h.date === dateStr);
  if (holiday) {
    return {
      status: 'HOLIDAY',
      name: holiday.name
    };
  }

  // 2. Check if Approved Leave first to capture Half Day leaves
  const leave = approvedLeaves.find(l => {
    if (l.status !== 'approved') return false;
    const from = new Date(l.fromDate);
    const to = new Date(l.toDate);
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);
    return checkDate >= from && checkDate <= to;
  });

  // 3. Check if Present (Attendance Record Exists)
  const attendance = attendanceRecords.find(r => r.date === dateStr);
  if (attendance) {
    const todayObj = new Date();
    const todayStr = `${todayObj.getFullYear()}-${String(todayObj.getMonth() + 1).padStart(2, '0')}-${String(todayObj.getDate()).padStart(2, '0')}`;

    if (!attendance.logoutTime) {
      if (dateStr === todayStr) {
        return {
          status: 'WORKING',
          record: attendance,
          leave: leave
        };
      } else if (dateStr < todayStr) {
        return {
          status: 'MISSING_LOGOUT',
          record: attendance,
          leave: leave
        };
      }
    }

    const minutes = attendance.totalWorkMinutes || 0;
    // If they have Half Day leave, expected is 4h 15m (like Saturday)
    const expected = (isSaturday || (leave && leave.leaveType === 'Half Day')) ? EXPECTED_MINUTES_SATURDAY : EXPECTED_MINUTES_WEEKDAY;
    const isHalfDay = minutes < expected;
    
    return {
      status: 'PRESENT',
      record: attendance,
      minutes,
      expected,
      isHalfDay,
      leave: leave
    };
  }
  
  if (leave) {
    return {
      status: 'LEAVE',
      leave: leave
    };
  }
  
  // 3. Sunday is Weekend
  if (isSunday) {
    return { status: 'WEEKEND' };
  }
  
  // 4. Future days are just empty (neither absent nor weekend unless it's Sunday)
  if (isFuture) {
    return { status: 'FUTURE' };
  }
  
  // 5. Past days without attendance or leave are ABSENT
  // Since Saturday is a working day (half day), it is marked as Absent if they didn't show up
  return { 
    status: 'ABSENT',
    isSaturday
  };
};
